import { useState, useEffect, useCallback, useRef } from 'react'
import { getData, updateData } from '../utils/storage'
import { NewTaskSchema, validate } from '../data/validation'
import { queueTaskSync } from '../lib/sync'
import type { Task, TaskStatus, TimeOfDay } from '../data/types'

// Filter options for task filtering
export interface TaskFilters {
  day?: 'today' | 'tomorrow' | 'week'
  timeOfDay?: TimeOfDay
  month?: number  // 0-11
}

export type SortOption = 'manual' | 'dueDate' | 'priority' | 'energy' | 'created'

interface UndoEntry {
  taskId: string
  task: Task
  action: 'complete' | 'delete'
  timestamp: number
}

type NewTask = Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'order' | 'status'>

export function useTasks() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const undoQueueRef = useRef<UndoEntry[]>([])

  // Load tasks from storage
  useEffect(() => {
    async function loadTasks() {
      try {
        const data = await getData()
        setTasks(data.tasks)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load tasks'))
      } finally {
        setIsLoading(false)
      }
    }
    loadTasks()
  }, [])

  // Add a new task — reads order from DB, not closure
  const addTask = useCallback(async (taskData: NewTask): Promise<Task> => {
    const validation = validate(NewTaskSchema, taskData)
    if (!validation.success) {
      throw new Error(`Invalid task: ${validation.error}`)
    }

    const now = new Date().toISOString()
    const newTask: Task = {
      ...validation.data,
      id: crypto.randomUUID(),
      status: 'pending',
      createdAt: now,
      updatedAt: now,
      order: 0 // Set from DB below
    }

    const updated = await updateData(data => {
      newTask.order = data.tasks.length
      return { ...data, tasks: [...data.tasks, newTask] }
    })

    setTasks(updated.tasks)
    queueTaskSync(newTask, 'upsert')
    return newTask
  }, [])

  // Update a task — finds task from DB, not closure
  const updateTask = useCallback(async (id: string, updates: Partial<Task>): Promise<void> => {
    const now = new Date().toISOString()
    let updatedTask: Task | undefined

    const updated = await updateData(data => {
      const existing = data.tasks.find(t => t.id === id)
      if (!existing) return data
      updatedTask = { ...existing, ...updates, updatedAt: now }
      return {
        ...data,
        tasks: data.tasks.map(t => t.id === id ? updatedTask! : t)
      }
    })

    setTasks(updated.tasks)
    if (updatedTask) queueTaskSync(updatedTask, 'upsert')
  }, [])

  // Complete a task (with undo support) — reads from DB
  const completeTask = useCallback(async (id: string, duration?: number): Promise<void> => {
    const now = new Date().toISOString()
    let completedTask: Task | undefined

    const updated = await updateData(data => {
      const task = data.tasks.find(t => t.id === id)
      if (!task) return data

      // Push to undo queue (prune expired, keep last 5)
      const freshQueue = undoQueueRef.current.filter(e => Date.now() - e.timestamp <= 10000)
      undoQueueRef.current = [
        { taskId: id, task: { ...task }, action: 'complete', timestamp: Date.now() },
        ...freshQueue.slice(0, 4)
      ]

      completedTask = { ...task, status: 'completed' as TaskStatus, updatedAt: now }
      return {
        ...data,
        tasks: data.tasks.map(t => t.id === id ? completedTask! : t),
        completedTasks: [
          ...data.completedTasks,
          { id: crypto.randomUUID(), taskId: id, completedAt: now, duration: duration || 0 }
        ]
      }
    })

    setTasks(updated.tasks)
    if (completedTask) queueTaskSync(completedTask, 'upsert')
  }, [])

  // Defer a task — reads from DB
  const deferTask = useCallback(async (id: string, until?: string): Promise<void> => {
    const now = new Date().toISOString()
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    const deferredUntil = until || tomorrow.toISOString().split('T')[0]
    let deferredTask: Task | undefined

    const updated = await updateData(data => {
      const task = data.tasks.find(t => t.id === id)
      if (!task) return data
      deferredTask = { ...task, status: 'deferred' as TaskStatus, deferredUntil, updatedAt: now }
      return {
        ...data,
        tasks: data.tasks.map(t => t.id === id ? deferredTask! : t)
      }
    })

    setTasks(updated.tasks)
    if (deferredTask) queueTaskSync(deferredTask, 'upsert')
  }, [])

  // Snooze a task — reads maxOrder from DB
  const snoozeTask = useCallback(async (id: string): Promise<void> => {
    const now = new Date().toISOString()

    const updated = await updateData(data => {
      const maxOrder = Math.max(...data.tasks.map(t => t.order), 0)
      return {
        ...data,
        tasks: data.tasks.map(task =>
          task.id === id ? { ...task, order: maxOrder + 1, updatedAt: now } : task
        )
      }
    })

    setTasks(updated.tasks)
  }, [])

  // Delete a task (with undo support) — reads from DB
  const deleteTask = useCallback(async (id: string): Promise<void> => {
    let deletedTask: Task | undefined

    const updated = await updateData(data => {
      deletedTask = data.tasks.find(t => t.id === id)

      if (deletedTask) {
        const freshQueue = undoQueueRef.current.filter(e => Date.now() - e.timestamp <= 10000)
        undoQueueRef.current = [
          { taskId: id, task: { ...deletedTask }, action: 'delete', timestamp: Date.now() },
          ...freshQueue.slice(0, 4)
        ]
      }

      return {
        ...data,
        tasks: data.tasks.filter(t => t.id !== id)
      }
    })

    setTasks(updated.tasks)
    if (deletedTask) queueTaskSync(deletedTask, 'delete')
  }, [])

  // Reorder tasks (bulk)
  const reorderTasks = useCallback(async (reorderedTasks: Task[]): Promise<void> => {
    const now = new Date().toISOString()
    const withNewOrder = reorderedTasks.map((task, index) => ({
      ...task,
      order: index,
      updatedAt: now
    }))

    const updated = await updateData(data => ({
      ...data,
      tasks: withNewOrder
    }))

    setTasks(updated.tasks)
  }, [])

  // Reorder a single task — reads from DB
  const reorderTask = useCallback(async (taskId: string, toIndex: number): Promise<void> => {
    const now = new Date().toISOString()

    const updated = await updateData(data => {
      const taskIndex = data.tasks.findIndex(t => t.id === taskId)
      if (taskIndex === -1) return data

      const reordered = [...data.tasks]
      const [removed] = reordered.splice(taskIndex, 1)
      reordered.splice(toIndex, 0, removed)

      return {
        ...data,
        tasks: reordered.map((task, index) => ({
          ...task,
          order: index,
          updatedAt: now
        }))
      }
    })

    setTasks(updated.tasks)
  }, [])

  // Refresh tasks from storage
  const refreshTasks = useCallback(async (): Promise<void> => {
    const data = await getData()
    setTasks(data.tasks)
  }, [])

  // Derived state
  const pendingTasks = tasks.filter(t => t.status === 'pending')
  const completedTasks = tasks.filter(t => t.status === 'completed')
  const deferredTasks = tasks.filter(t => t.status === 'deferred')

  const getTasksByProject = useCallback((projectId: string): Task[] => {
    return tasks.filter(t => t.projectId === projectId)
  }, [tasks])

  const getNextTaskForProject = useCallback((projectId: string): Task | undefined => {
    return tasks
      .filter(t => t.projectId === projectId && t.status === 'pending')
      .sort((a, b) => a.order - b.order)[0]
  }, [tasks])

  const searchTasks = useCallback((query: string): Task[] => {
    if (!query.trim()) return tasks
    const q = query.toLowerCase()
    return tasks.filter(t =>
      t.verbLabel.toLowerCase().includes(q) ||
      t.taskBody.toLowerCase().includes(q) ||
      (t.notes && t.notes.toLowerCase().includes(q)) ||
      (t.tags && t.tags.some(tag => tag.toLowerCase().includes(q)))
    )
  }, [tasks])

  const sortTasks = useCallback((taskList: Task[], by: SortOption): Task[] => {
    const sorted = [...taskList]
    switch (by) {
    case 'manual':
      return sorted.sort((a, b) => a.order - b.order)
    case 'dueDate':
      return sorted.sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return a.order - b.order
        if (!a.dueDate) return 1
        if (!b.dueDate) return -1
        return a.dueDate.localeCompare(b.dueDate)
      })
    case 'priority':
      return sorted.sort((a, b) => (b.priority || 3) - (a.priority || 3))
    case 'energy':
      return sorted.sort((a, b) => {
        const energyOrder = { low: 0, medium: 1, high: 2 }
        return energyOrder[a.feedLevel] - energyOrder[b.feedLevel]
      })
    case 'created':
      return sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    default:
      return sorted
    }
  }, [])

  // Undo last action — looks up by task ID, not position
  const undoLastAction = useCallback(async (): Promise<boolean> => {
    // Prune expired
    undoQueueRef.current = undoQueueRef.current.filter(e => Date.now() - e.timestamp <= 10000)

    const entry = undoQueueRef.current[0]
    if (!entry) return false

    undoQueueRef.current = undoQueueRef.current.slice(1)

    const restoredTask = { ...entry.task, updatedAt: new Date().toISOString() }

    if (entry.action === 'complete') {
      restoredTask.status = 'pending' as TaskStatus
      const updated = await updateData(data => ({
        ...data,
        tasks: data.tasks.map(t => t.id === restoredTask.id ? restoredTask : t),
        completedTasks: data.completedTasks.filter(ct => ct.taskId !== restoredTask.id)
      }))
      setTasks(updated.tasks)
    } else {
      const updated = await updateData(data => ({
        ...data,
        tasks: [...data.tasks, restoredTask]
      }))
      setTasks(updated.tasks)
    }

    queueTaskSync(restoredTask, 'upsert')
    return true
  }, [])

  // Duplicate a task — reads from DB
  const duplicateTask = useCallback(async (id: string): Promise<Task | undefined> => {
    const now = new Date().toISOString()
    let newTask: Task | undefined

    const updated = await updateData(data => {
      const task = data.tasks.find(t => t.id === id)
      if (!task) return data

      newTask = {
        ...task,
        id: crypto.randomUUID(),
        status: 'pending',
        createdAt: now,
        updatedAt: now,
        order: data.tasks.length,
        subtasks: task.subtasks?.map(s => ({ ...s, id: crypto.randomUUID(), completed: false }))
      }

      return { ...data, tasks: [...data.tasks, newTask] }
    })

    setTasks(updated.tasks)
    if (newTask) queueTaskSync(newTask, 'upsert')
    return newTask
  }, [])

  // Add a subtask — reads from DB
  const addSubtask = useCallback(async (taskId: string, text: string): Promise<void> => {
    const subtask = { id: crypto.randomUUID(), text, completed: false }
    const now = new Date().toISOString()

    const updated = await updateData(data => {
      const task = data.tasks.find(t => t.id === taskId)
      if (!task) return data
      const updatedSubtasks = [...(task.subtasks || []), subtask]
      return {
        ...data,
        tasks: data.tasks.map(t =>
          t.id === taskId ? { ...t, subtasks: updatedSubtasks, updatedAt: now } : t
        )
      }
    })

    setTasks(updated.tasks)
  }, [])

  // Toggle a subtask — reads from DB
  const toggleSubtask = useCallback(async (taskId: string, subtaskId: string): Promise<void> => {
    const now = new Date().toISOString()

    const updated = await updateData(data => {
      const task = data.tasks.find(t => t.id === taskId)
      if (!task || !task.subtasks) return data
      const updatedSubtasks = task.subtasks.map(s =>
        s.id === subtaskId ? { ...s, completed: !s.completed } : s
      )
      return {
        ...data,
        tasks: data.tasks.map(t =>
          t.id === taskId ? { ...t, subtasks: updatedSubtasks, updatedAt: now } : t
        )
      }
    })

    setTasks(updated.tasks)
  }, [])

  // Delete a subtask — reads from DB
  const deleteSubtask = useCallback(async (taskId: string, subtaskId: string): Promise<void> => {
    const now = new Date().toISOString()

    const updated = await updateData(data => {
      const task = data.tasks.find(t => t.id === taskId)
      if (!task || !task.subtasks) return data
      const updatedSubtasks = task.subtasks.filter(s => s.id !== subtaskId)
      return {
        ...data,
        tasks: data.tasks.map(t =>
          t.id === taskId ? { ...t, subtasks: updatedSubtasks, updatedAt: now } : t
        )
      }
    })

    setTasks(updated.tasks)
  }, [])

  const getOverdueTasks = useCallback((): Task[] => {
    const today = new Date().toISOString().split('T')[0]
    return tasks.filter(t =>
      t.status === 'pending' && t.dueDate && t.dueDate < today
    )
  }, [tasks])

  const canUndo = useCallback((): boolean => {
    const entry = undoQueueRef.current[0]
    return !!entry && Date.now() - entry.timestamp <= 10000
  }, [])

  const filterTasks = useCallback((taskList: Task[], filters: TaskFilters): Task[] => {
    let filtered = [...taskList]

    if (filters.day) {
      const now = new Date()
      const today = now.toISOString().split('T')[0]
      const tomorrow = new Date(now.getTime() + 86400000).toISOString().split('T')[0]
      const weekFromNow = new Date(now.getTime() + 7 * 86400000).toISOString().split('T')[0]

      filtered = filtered.filter(task => {
        const taskDate = task.scheduledFor || task.createdAt.split('T')[0]
        switch (filters.day) {
        case 'today':
          return taskDate === today
        case 'tomorrow':
          return taskDate === tomorrow
        case 'week':
          return taskDate >= today && taskDate <= weekFromNow
        default:
          return true
        }
      })
    }

    if (filters.timeOfDay) {
      filtered = filtered.filter(task =>
        task.timeOfDay === filters.timeOfDay || task.timeOfDay === 'anytime'
      )
    }

    if (filters.month !== undefined) {
      filtered = filtered.filter(task => {
        const taskDate = task.scheduledFor || task.createdAt.split('T')[0]
        const taskMonth = new Date(taskDate).getMonth()
        return taskMonth === filters.month
      })
    }

    return filtered
  }, [])

  return {
    tasks,
    pendingTasks,
    completedTasks,
    deferredTasks,
    isLoading,
    error,
    addTask,
    updateTask,
    completeTask,
    deferTask,
    snoozeTask,
    deleteTask,
    reorderTasks,
    reorderTask,
    refreshTasks,
    getTasksByProject,
    getNextTaskForProject,
    filterTasks,
    searchTasks,
    sortTasks,
    undoLastAction,
    canUndo,
    duplicateTask,
    addSubtask,
    toggleSubtask,
    deleteSubtask,
    getOverdueTasks
  }
}
